import {
  REPORT_CHART_BAR_PALETTE,
  REPORT_CHART_BAR_PALETTE_REFRESH_OFFSET,
  REPORT_CHART_DEFAULT_DISPLAY_MODE,
  REPORT_CHART_VALUE_COLOR_ALPHA,
  REPORT_CHART_VALUE_GRADATION_COLORS,
  REPORT_CHART_VALUE_GRADATION_COUNT,
  REPORT_CHART_VALUE_GRADATION_EXPONENT,
} from "./report-chart.constants";
import { ReportChartDisplayMode } from "./report-chart.models";

const getValueGradationIndex = ({
  value,
  maxValue,
}: {
  value: number;
  maxValue: number;
}): number => {
  if (maxValue <= 0 || value <= 0) {
    return 0;
  }

  const pct = Math.max(0, Math.min(100, (value / maxValue) * 100));
  const tScaled = Math.pow(pct / 100, REPORT_CHART_VALUE_GRADATION_EXPONENT);

  if (pct >= 100) {
    return REPORT_CHART_VALUE_GRADATION_COUNT - 1;
  }

  return Math.min(
    REPORT_CHART_VALUE_GRADATION_COUNT - 1,
    Math.floor(tScaled * REPORT_CHART_VALUE_GRADATION_COUNT),
  );
};

const valueGradationColorToRgba = ({
  rgb,
  alpha,
}: {
  rgb: readonly [number, number, number];
  alpha: number;
}): string => {
  const [red, green, blue] = rgb;
  return `rgba(${red},${green},${blue},${alpha})`;
};

export const colorByReportChartValue = ({
  value,
  maxValue,
  alpha = REPORT_CHART_VALUE_COLOR_ALPHA,
}: {
  value: number;
  maxValue: number;
  alpha?: number;
}): string => {
  const gradationIndex = getValueGradationIndex({ value, maxValue });
  const rgb = REPORT_CHART_VALUE_GRADATION_COLORS[gradationIndex];

  return valueGradationColorToRgba({ rgb, alpha });
};

export const getDistinctBarColor = ({
  index,
  refreshSeed,
}: {
  index: number;
  refreshSeed: number;
}): string => {
  const paletteIndex =
    (index + refreshSeed * REPORT_CHART_BAR_PALETTE_REFRESH_OFFSET) %
    REPORT_CHART_BAR_PALETTE.length;
  return REPORT_CHART_BAR_PALETTE[paletteIndex];
};

export const getBarBackgroundColors = ({
  displayMode,
  values,
  maxValue,
  refreshSeed,
}: {
  displayMode:
    | ReportChartDisplayMode.colorByValue
    | ReportChartDisplayMode.differentColors;
  values: number[];
  maxValue: number;
  refreshSeed: number;
}): string[] => {
  if (displayMode === ReportChartDisplayMode.differentColors) {
    return values.map((_, index) =>
      getDistinctBarColor({ index, refreshSeed }),
    );
  }

  return values.map(value => colorByReportChartValue({ value, maxValue }));
};

export const isReportChartLineMode = (
  displayMode: ReportChartDisplayMode,
): boolean => displayMode === ReportChartDisplayMode.lineChart;

export const isReportChartPieMode = (
  displayMode: ReportChartDisplayMode,
): boolean => displayMode === ReportChartDisplayMode.pieChart;

export const isReportChartCartesianMode = (
  displayMode: ReportChartDisplayMode,
): boolean => !isReportChartPieMode(displayMode);

export const getPieSliceColors = ({
  sliceCount,
  refreshSeed,
}: {
  sliceCount: number;
  refreshSeed: number;
}): string[] =>
  Array.from({ length: sliceCount }, (_, index) =>
    getDistinctBarColor({ index, refreshSeed }),
  );

export const parseReportChartMaxItems = (input: string): number | undefined => {
  const trimmed = input.trim();
  if (!trimmed) {
    return undefined;
  }

  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }

  return parsed;
};

export const normalizeReportChartDisplayMode = (
  value: string,
): ReportChartDisplayMode => {
  const chartDisplayModes = Object.values(
    ReportChartDisplayMode,
  ) as ReportChartDisplayMode[];

  if (chartDisplayModes.includes(value as ReportChartDisplayMode)) {
    return value as ReportChartDisplayMode;
  }

  return REPORT_CHART_DEFAULT_DISPLAY_MODE;
};
